import React, { useState } from 'react';
import { GripIcon, PlusCircleIcon, XIcon, EyeIcon, AlertCircleIcon, BrainIcon, CheckCircleIcon, InfoIcon, CalendarIcon, FileTextIcon, MailIcon, PhoneIcon, ListIcon, CheckSquareIcon, ToggleRightIcon, FileIcon, ArrowRightIcon, Settings2Icon, FilterIcon, ZapIcon } from 'lucide-react';
interface Step3ApplicationFormProps {
  formData: any;
  updateFormData: (data: any) => void;
}
interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
  eligibilityEnabled?: boolean;
  eligibilityRules?: {
    condition: string;
    values: string[];
  };
}
const Step3ApplicationForm: React.FC<Step3ApplicationFormProps> = ({
  formData,
  updateFormData
}) => {
  // Default form fields based on program type
  const defaultFields: FormField[] = [{
    id: 'fullName',
    type: 'text',
    label: 'Full Name',
    required: true,
    placeholder: 'Enter your full name'
  }, {
    id: 'email',
    type: 'email',
    label: 'Email Address',
    required: true,
    placeholder: 'Enter your email address'
  }, {
    id: 'phone',
    type: 'tel',
    label: 'Phone Number',
    required: true,
    placeholder: 'Enter your phone number'
  }, {
    id: 'nationality',
    type: 'select',
    label: 'Nationality',
    required: true,
    options: ['UAE', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Other']
  }, {
    id: 'education',
    type: 'select',
    label: 'Highest Education Level',
    required: true,
    options: ['High School', "Bachelor's Degree", "Master's Degree", 'Doctorate', 'Other']
  }];
  const [fields, setFields] = useState<FormField[]>(formData.formFields?.length ? formData.formFields : defaultFields);
  const [showAiSuggestions, setShowAiSuggestions] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [testingEligibility, setTestingEligibility] = useState(false);
  const [testResponses, setTestResponses] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState('builder');
  const fieldTypes = [{
    value: 'text',
    label: 'Text Field',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-884" />,
    tooltip: 'Use for short text responses like names or addresses',
    eligibilityCompatible: false
  }, {
    value: 'email',
    label: 'Email Field',
    icon: <MailIcon className="w-4 h-4" data-id="element-885" />,
    tooltip: 'Validates email format automatically',
    eligibilityCompatible: false
  }, {
    value: 'tel',
    label: 'Phone Field',
    icon: <PhoneIcon className="w-4 h-4" data-id="element-886" />,
    tooltip: 'Optimized for phone number input',
    eligibilityCompatible: false
  }, {
    value: 'select',
    label: 'Dropdown',
    icon: <ListIcon className="w-4 h-4" data-id="element-887" />,
    tooltip: 'Use for lists with 5+ options. Great for compact mobile layouts',
    eligibilityCompatible: true
  }, {
    value: 'checkbox',
    label: 'Checkboxes',
    icon: <CheckSquareIcon className="w-4 h-4" data-id="element-888" />,
    tooltip: 'Use when multiple selections are allowed',
    eligibilityCompatible: true
  }, {
    value: 'radio',
    label: 'Radio Buttons',
    icon: <ToggleRightIcon className="w-4 h-4" data-id="element-889" />,
    tooltip: 'Use when only one selection is allowed from a list',
    eligibilityCompatible: true
  }, {
    value: 'file',
    label: 'File Upload',
    icon: <FileIcon className="w-4 h-4" data-id="element-890" />,
    tooltip: 'Allow students to upload documents or certificates',
    eligibilityCompatible: false
  }, {
    value: 'date',
    label: 'Date Picker',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-891" />,
    tooltip: 'Optimized for selecting dates',
    eligibilityCompatible: true
  }, {
    value: 'textarea',
    label: 'Text Area',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-892" />,
    tooltip: 'Use for longer text responses like personal statements',
    eligibilityCompatible: false
  }];
  const aiSuggestions = [{
    id: 'language_certificate',
    title: 'Language Proficiency Certificate',
    description: 'Students in similar programs often require a language certificate (IELTS, TOEFL)',
    field: {
      id: `field_${Date.now()}_language`,
      type: 'file',
      label: 'Language Proficiency Certificate',
      required: true,
      placeholder: 'Upload your IELTS, TOEFL, or other language certificate'
    }
  }, {
    id: 'work_experience',
    title: 'Work Experience',
    description: 'For graduate programs, work experience is often a key eligibility factor',
    field: {
      id: `field_${Date.now()}_experience`,
      type: 'select',
      label: 'Years of Work Experience',
      required: true,
      options: ['None', '1-2 years', '3-5 years', '5+ years'],
      eligibilityEnabled: true,
      eligibilityRules: {
        condition: 'equals',
        values: ['3-5 years', '5+ years']
      }
    }
  }];
  const handleAddField = () => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      required: false,
      placeholder: 'Enter value'
    };
    const updatedFields = [...fields, newField];
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
  };
  const handleAddAiSuggestion = (suggestion: any) => {
    const updatedFields = [...fields, suggestion.field];
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
    // Remove the suggestion from visible suggestions
    setShowAiSuggestions(false);
  };
  const handleRemoveField = (id: string) => {
    const updatedFields = fields.filter(field => field.id !== id);
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
  };
  const handleFieldChange = (id: string, property: keyof FormField, value: any) => {
    const updatedFields = fields.map(field => {
      if (field.id === id) {
        return {
          ...field,
          [property]: value
        };
      }
      return field;
    });
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
  };
  const toggleEligibility = (id: string, enabled: boolean) => {
    const updatedFields = fields.map(field => {
      if (field.id === id) {
        return {
          ...field,
          eligibilityEnabled: enabled,
          eligibilityRules: enabled ? field.eligibilityRules || {
            condition: 'equals',
            values: field.type === 'select' || field.type === 'radio' ? [field.options?.[0] || ''] : []
          } : undefined
        };
      }
      return field;
    });
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
    if (enabled) {
      setEditingEligibility(id);
    } else {
      setEditingEligibility(null);
    }
  };
  const updateEligibilityRule = (id: string, property: keyof FormField['eligibilityRules'], value: any) => {
    const updatedFields = fields.map(field => {
      if (field.id === id && field.eligibilityEnabled) {
        return {
          ...field,
          eligibilityRules: {
            ...(field.eligibilityRules || {
              condition: 'equals',
              values: []
            }),
            [property]: value
          }
        };
      }
      return field;
    });
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
  };
  const isFieldEligible = (field: FormField) => {
    if (!field.eligibilityEnabled || !field.eligibilityRules) return true;
    const response = testResponses[field.id];
    if (!response) return true;
    const {
      condition,
      values
    } = field.eligibilityRules;
    if (field.type === 'select' || field.type === 'radio') {
      if (condition === 'equals') {
        return values.includes(response);
      }
    } else if (field.type === 'checkbox') {
      // For checkboxes, check if any selected option is in the eligible values
      return response.some((selected: string) => values.includes(selected));
    } else if (field.type === 'date') {
      // Simple date comparison (could be enhanced for more complex rules)
      const responseDate = new Date(response);
      if (condition === 'after') {
        const targetDate = new Date(values[0]);
        return responseDate > targetDate;
      } else if (condition === 'before') {
        const targetDate = new Date(values[0]);
        return responseDate < targetDate;
      }
    }
    return true;
  };
  const getFieldTypeIcon = (type: string) => {
    const fieldType = fieldTypes.find(ft => ft.value === type);
    return fieldType?.icon || <FileTextIcon className="w-4 h-4" data-id="element-893" />;
  };
  const isEligibilityCompatible = (type: string) => {
    const fieldType = fieldTypes.find(ft => ft.value === type);
    return fieldType?.eligibilityCompatible || false;
  };
  // Render form field based on type
  const renderFormField = (field: FormField) => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return <input type={field.type} placeholder={field.placeholder} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} value={testingEligibility ? testResponses[field.id] || '' : ''} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.value
        })} data-id="element-894" />;
      case 'textarea':
        return <textarea placeholder={field.placeholder} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} value={testingEligibility ? testResponses[field.id] || '' : ''} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.value
        })} data-id="element-895" />;
      case 'select':
        return <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} value={testingEligibility ? testResponses[field.id] || '' : ''} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.value
        })} data-id="element-896">
            <option value="" data-id="element-897">Select an option</option>
            {field.options?.map((option, index) => <option key={index} value={option} data-id="element-898">
                {option}
              </option>)}
          </select>;
      case 'file':
        return <input type="file" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.files?.[0]?.name || 'file.pdf'
        })} data-id="element-899" />;
      case 'checkbox':
        return <div className="space-y-2" data-id="element-900">
            {field.options?.map((option, index) => <div key={index} className="flex items-center" data-id="element-901">
                <input type="checkbox" name={field.id} value={option} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" disabled={!testingEligibility && showPreview} checked={testingEligibility ? (testResponses[field.id] || []).includes(option) : false} onChange={e => {
              const currentValues = testResponses[field.id] || [];
              const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter((v: string) => v !== option);
              setTestResponses({
                ...testResponses,
                [field.id]: newValues
              });
            }} data-id="element-902" />
                <span className="ml-2 text-sm text-gray-600" data-id="element-903">{option}</span>
              </div>)}
          </div>;
      case 'radio':
        return <div className="space-y-2" data-id="element-904">
            {field.options?.map((option, index) => <div key={index} className="flex items-center" data-id="element-905">
                <input type="radio" name={field.id} value={option} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300" disabled={!testingEligibility && showPreview} checked={testingEligibility ? testResponses[field.id] === option : false} onChange={e => setTestResponses({
              ...testResponses,
              [field.id]: option
            })} data-id="element-906" />
                <span className="ml-2 text-sm text-gray-600" data-id="element-907">{option}</span>
              </div>)}
          </div>;
      case 'date':
        return <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} value={testingEligibility ? testResponses[field.id] || '' : ''} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.value
        })} data-id="element-908" />;
      default:
        return null;
    }
  };
  const renderEligibilityEditor = (field: FormField) => {
    if (!field.eligibilityEnabled) return null;
    switch (field.type) {
      case 'select':
      case 'radio':
        return <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200" data-id="element-909">
            <div className="mb-3" data-id="element-910">
              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-911">
                Eligible if answer is:
              </label>
              <div className="space-y-2 ml-2" data-id="element-912">
                {field.options?.map((option, index) => <div key={index} className="flex items-center" data-id="element-913">
                    <input type="checkbox" id={`eligibility-${field.id}-${index}`} checked={field.eligibilityRules?.values.includes(option) || false} onChange={e => {
                  const currentValues = field.eligibilityRules?.values || [];
                  const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter(v => v !== option);
                  updateEligibilityRule(field.id, 'values', newValues);
                }} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" data-id="element-914" />
                    <label htmlFor={`eligibility-${field.id}-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-915">
                      {option}
                    </label>
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 flex items-center mt-2 bg-white p-2 rounded border border-gray-200" data-id="element-916">
              <AlertCircleIcon className="w-4 h-4 mr-2 text-primary-500" data-id="element-917" />
              Applicants will only be eligible if they select one of the checked
              options.
            </div>
          </div>;
      case 'checkbox':
        return <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200" data-id="element-918">
            <div className="mb-3" data-id="element-919">
              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-920">
                Eligible if any of these are selected:
              </label>
              <div className="space-y-2 ml-2" data-id="element-921">
                {field.options?.map((option, index) => <div key={index} className="flex items-center" data-id="element-922">
                    <input type="checkbox" id={`eligibility-${field.id}-${index}`} checked={field.eligibilityRules?.values.includes(option) || false} onChange={e => {
                  const currentValues = field.eligibilityRules?.values || [];
                  const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter(v => v !== option);
                  updateEligibilityRule(field.id, 'values', newValues);
                }} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" data-id="element-923" />
                    <label htmlFor={`eligibility-${field.id}-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-924">
                      {option}
                    </label>
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 flex items-center mt-2 bg-white p-2 rounded border border-gray-200" data-id="element-925">
              <AlertCircleIcon className="w-4 h-4 mr-2 text-primary-500" data-id="element-926" />
              Applicants will be eligible if they select at least one of the
              checked options.
            </div>
          </div>;
      case 'date':
        return <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200" data-id="element-927">
            <div className="mb-3" data-id="element-928">
              <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-929">
                Eligible if date is:
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-930">
                <select value={field.eligibilityRules?.condition || 'after'} onChange={e => updateEligibilityRule(field.id, 'condition', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-931">
                  <option value="after" data-id="element-932">After</option>
                  <option value="before" data-id="element-933">Before</option>
                </select>
                <input type="date" value={field.eligibilityRules?.values[0] || ''} onChange={e => {
                updateEligibilityRule(field.id, 'values', [e.target.value]);
              }} className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-934" />
              </div>
            </div>
            <div className="text-xs text-gray-500 flex items-center mt-2 bg-white p-2 rounded border border-gray-200" data-id="element-935">
              <AlertCircleIcon className="w-4 h-4 mr-2 text-primary-500" data-id="element-936" />
              Applicants will only be eligible if they select a date that meets
              this condition.
            </div>
          </div>;
      default:
        return null;
    }
  };
  const [editingEligibility, setEditingEligibility] = useState<string | null>(null);
  return <div data-id="element-937">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-938">
        Application Form Builder
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-939">
        Customize the application form students will complete to apply to this
        scholarship.
      </p>
      {/* Tab navigation */}
      
      {/* AI Suggestions */}
      {showAiSuggestions && activeTab === 'builder' && <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6" data-id="element-940">
          <div className="flex items-start" data-id="element-941">
            <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3" data-id="element-942">
              <BrainIcon className="w-5 h-5 text-blue-600" data-id="element-943" />
            </div>
            <div className="flex-1" data-id="element-944">
              <h3 className="font-medium text-blue-800 mb-1" data-id="element-945">
                AI Form Field Suggestions
              </h3>
              <p className="text-sm text-blue-700 mb-4" data-id="element-946">
                Based on similar programs, we recommend adding these fields to
                your application form:
              </p>
              <div className="space-y-3" data-id="element-947">
                {aiSuggestions.map(suggestion => <div key={suggestion.id} className="bg-white border border-blue-200 rounded-md p-3 flex justify-between items-center" data-id="element-948">
                    <div data-id="element-949">
                      <h4 className="text-sm font-medium text-gray-800" data-id="element-950">
                        {suggestion.title}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-951">
                        {suggestion.description}
                      </p>
                    </div>
                    <button onClick={() => handleAddAiSuggestion(suggestion)} className="text-blue-600 hover:bg-blue-50 rounded-md px-3 py-1 text-sm font-medium flex items-center" data-id="element-952">
                      <PlusCircleIcon className="w-4 h-4 mr-1" data-id="element-953" />
                      Add Field
                    </button>
                  </div>)}
              </div>
              <div className="mt-3 flex justify-end" data-id="element-954">
                <button onClick={() => setShowAiSuggestions(false)} className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-955">
                  Dismiss suggestions
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Form Builder Controls */}
      {activeTab === 'builder' && <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6" data-id="element-956">
          <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200 bg-gray-50" data-id="element-957">
            <div className="flex items-center" data-id="element-958">
              <FileTextIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-959" />
              <h3 className="text-md font-medium text-gray-800" data-id="element-960">Form Fields</h3>
            </div>
            <div className="flex space-x-2" data-id="element-961">
              {testingEligibility ? <button type="button" onClick={() => setTestingEligibility(false)} className="px-3 py-1.5 bg-primary-500 text-white text-sm rounded-md flex items-center" data-id="element-962">
                  <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-963" />
                  Exit Test Mode
                </button> : <>
                  <button type="button" onClick={() => {
              setTestingEligibility(true);
              setShowPreview(true);
              setTestResponses({});
            }} className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm rounded-md flex items-center hover:bg-gray-50" disabled={showPreview} data-id="element-964">
                    <FilterIcon className="w-4 h-4 mr-1.5" data-id="element-965" />
                    Test Eligibility
                  </button>
                  <button type="button" onClick={() => setShowPreview(!showPreview)} className={`px-3 py-1.5 ${showPreview ? 'bg-primary-500 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'} text-sm rounded-md flex items-center`} data-id="element-966">
                    <EyeIcon className="w-4 h-4 mr-1.5" data-id="element-967" />
                    {showPreview ? 'Exit Preview' : 'Preview Form'}
                  </button>
                </>}
              <button type="button" onClick={handleAddField} className="px-3 py-1.5 bg-primary-500 text-white text-sm rounded-md flex items-center hover:bg-primary-600" disabled={showPreview && !testingEligibility} data-id="element-968">
                <PlusCircleIcon className="w-4 h-4 mr-1.5" data-id="element-969" />
                Add Field
              </button>
            </div>
          </div>
          {/* Preview mode notice */}
          {showPreview && !testingEligibility && <div className="bg-yellow-50 border-b border-yellow-100 px-5 py-3" data-id="element-970">
              <div className="flex items-center text-sm text-yellow-800" data-id="element-971">
                <AlertCircleIcon className="w-4 h-4 mr-2 text-yellow-600" data-id="element-972" />
                You are in preview mode. This is how students will see the form.
              </div>
            </div>}
          {/* Test eligibility mode notice */}
          {testingEligibility && <div className="bg-blue-50 border-b border-blue-100 px-5 py-3" data-id="element-973">
              <div className="flex items-center text-sm text-blue-800" data-id="element-974">
                <ZapIcon className="w-4 h-4 mr-2 text-blue-600" data-id="element-975" />
                Test eligibility mode. Fill out the form to see which fields
                would be shown or hidden based on eligibility rules.
              </div>
            </div>}
          <div className="p-5" data-id="element-976">
            <div className="space-y-4" data-id="element-977">
              {fields.map((field, index) => {
            // In test mode, check if the field should be shown based on eligibility
            const isEligible = testingEligibility ? isFieldEligible(field) : true;
            return <div key={field.id} className={`rounded-lg border ${editingEligibility === field.id ? 'border-primary-500 ring-1 ring-primary-500' : testingEligibility && !isEligible ? 'border-red-200 bg-red-50' : 'border-gray-200 hover:border-gray-300'} ${showPreview ? 'bg-white' : 'bg-white'} transition-all duration-150 overflow-hidden`} data-id="element-978">
                    {!showPreview || testingEligibility ? <div className="p-4" data-id="element-979">
                        <div className="flex justify-between items-center mb-3" data-id="element-980">
                          <div className="flex items-center" data-id="element-981">
                            <div className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary-50 text-primary-500 text-sm font-medium mr-2" data-id="element-982">
                              {index + 1}
                            </div>
                            <div className="flex items-center" data-id="element-983">
                              <span className="text-sm font-medium text-gray-700 mr-2" data-id="element-984">
                                {getFieldTypeIcon(field.type)}
                              </span>
                              <span className="text-sm font-medium text-gray-700" data-id="element-985">
                                Field #{index + 1}
                              </span>
                              {field.eligibilityEnabled && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center" data-id="element-986">
                                  <FilterIcon className="w-3 h-3 mr-1" data-id="element-987" />
                                  Eligibility Filter
                                </span>}
                            </div>
                          </div>
                          <button type="button" onClick={() => handleRemoveField(field.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-988">
                            <XIcon className="w-5 h-5" data-id="element-989" />
                          </button>
                        </div>
                        {testingEligibility ? <div data-id="element-990">
                            {!isEligible && <div className="mb-3 p-2 bg-red-50 border border-red-100 rounded text-sm text-red-800 flex items-center" data-id="element-991">
                                <AlertCircleIcon className="w-4 h-4 mr-2 text-red-500" data-id="element-992" />
                                This field would be hidden based on eligibility
                                rules
                              </div>}
                            <label className={`block text-sm font-medium ${field.required ? 'text-gray-700' : 'text-gray-600'} mb-1`} data-id="element-993">
                              {field.label}{' '}
                              {field.required && <span className="text-red-500" data-id="element-994">*</span>}
                            </label>
                            {renderFormField(field)}
                          </div> : <div className="space-y-3" data-id="element-995">
                            <div className="grid grid-cols-2 gap-3" data-id="element-996">
                              <div data-id="element-997">
                                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-998">
                                  Field Label
                                </label>
                                <input type="text" value={field.label} onChange={e => handleFieldChange(field.id, 'label', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-999" />
                              </div>
                              <div data-id="element-1000">
                                <div className="flex items-center mb-1" data-id="element-1001">
                                  <label className="block text-xs font-medium text-gray-500" data-id="element-1002">
                                    Field Type
                                  </label>
                                  <div className="relative ml-1 group" data-id="element-1003">
                                    <InfoIcon className="w-3.5 h-3.5 text-gray-400 cursor-help" data-id="element-1004" />
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10" data-id="element-1005">
                                      {fieldTypes.find(ft => ft.value === field.type)?.tooltip}
                                    </div>
                                  </div>
                                </div>
                                <select value={field.type} onChange={e => handleFieldChange(field.id, 'type', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1006">
                                  {fieldTypes.map(type => <option key={type.value} value={type.value} data-id="element-1007">
                                      {type.label}
                                    </option>)}
                                </select>
                              </div>
                            </div>
                            {(field.type === 'text' || field.type === 'email' || field.type === 'tel' || field.type === 'textarea') && <div data-id="element-1008">
                                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1009">
                                  Placeholder Text
                                </label>
                                <input type="text" value={field.placeholder || ''} onChange={e => handleFieldChange(field.id, 'placeholder', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1010" />
                              </div>}
                            {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && <div data-id="element-1011">
                                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1012">
                                  Options (comma separated)
                                </label>
                                <input type="text" value={field.options?.join(', ') || ''} onChange={e => handleFieldChange(field.id, 'options', e.target.value.split(',').map(opt => opt.trim()))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Option 1, Option 2, Option 3" data-id="element-1013" />
                              </div>}
                            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-1" data-id="element-1014">
                              <div className="flex items-center" data-id="element-1015">
                                <input type="checkbox" id={`required-${field.id}`} checked={field.required} onChange={e => handleFieldChange(field.id, 'required', e.target.checked)} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" data-id="element-1016" />
                                <label htmlFor={`required-${field.id}`} className="ml-2 text-sm text-gray-700" data-id="element-1017">
                                  Required field
                                </label>
                              </div>
                              {isEligibilityCompatible(field.type) && <div className="flex items-center" data-id="element-1018">
                                  <input type="checkbox" id={`eligibility-toggle-${field.id}`} checked={field.eligibilityEnabled || false} onChange={e => toggleEligibility(field.id, e.target.checked)} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" data-id="element-1019" />
                                  <div className="flex items-center" data-id="element-1020">
                                    <label htmlFor={`eligibility-toggle-${field.id}`} className="ml-2 text-sm text-gray-700" data-id="element-1021">
                                      Use for eligibility
                                    </label>
                                    <div className="relative ml-1 group" data-id="element-1022">
                                      <InfoIcon className="w-3.5 h-3.5 text-gray-400 cursor-help" data-id="element-1023" />
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10" data-id="element-1024">
                                        Automatically screen applicants based on
                                        their responses to this question.
                                      </div>
                                    </div>
                                  </div>
                                </div>}
                            </div>
                            {/* Eligibility Rules Editor */}
                            {editingEligibility === field.id && renderEligibilityEditor(field)}
                            {field.eligibilityEnabled && editingEligibility !== field.id && <div className="mt-2 flex justify-end" data-id="element-1025">
                                  <button onClick={() => setEditingEligibility(field.id)} className="text-sm text-primary-500 flex items-center hover:underline" data-id="element-1026">
                                    <Settings2Icon className="w-3 h-3 mr-1" data-id="element-1027" />
                                    Edit eligibility rules
                                  </button>
                                </div>}
                          </div>}
                      </div> : <div className="p-4" data-id="element-1028">
                        <label className={`block text-sm font-medium ${field.required ? 'text-gray-700' : 'text-gray-600'} mb-1`} data-id="element-1029">
                          {field.label}{' '}
                          {field.required && <span className="text-red-500" data-id="element-1030">*</span>}
                        </label>
                        {renderFormField(field)}
                      </div>}
                  </div>;
          })}
            </div>
            {fields.length === 0 && <div className="text-center py-8 bg-white rounded-lg border border-dashed border-gray-300" data-id="element-1031">
                <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-1032">
                  <PlusCircleIcon className="w-6 h-6 text-gray-400" data-id="element-1033" />
                </div>
                <h3 className="text-gray-500 font-medium mb-1" data-id="element-1034">
                  No form fields added
                </h3>
                <p className="text-gray-400 text-sm mb-4" data-id="element-1035">
                  Start by adding fields to your enrollment form
                </p>
                <button type="button" onClick={handleAddField} className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors" data-id="element-1036">
                  Add First Field
                </button>
              </div>}
            {showPreview && fields.length > 0 && !testingEligibility && <div className="mt-6 flex justify-center" data-id="element-1037">
                <button type="button" className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors" disabled data-id="element-1038">
                  Submit Application
                </button>
              </div>}
          </div>
        </div>}
      {activeTab === 'preview' && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1039">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-1040">
            <h3 className="font-medium text-gray-700" data-id="element-1041">Form Preview</h3>
          </div>
          <div className="p-6 bg-white" data-id="element-1042">
            <div className="max-w-2xl mx-auto space-y-6" data-id="element-1043">
              {fields.map(field => <div key={field.id} className="mb-4" data-id="element-1044">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1045">
                    {field.label}{' '}
                    {field.required && <span className="text-red-500" data-id="element-1046">*</span>}
                  </label>
                  {renderFormField(field)}
                </div>)}
              <div className="pt-4 border-t border-gray-200" data-id="element-1047">
                <button type="button" className="px-4 py-2 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600" disabled data-id="element-1048">
                  Submit Application
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Guidance and notes */}
      {activeTab === 'builder' && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6" data-id="element-1049">
          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-1050">
            <InfoIcon className="w-4 h-4 mr-2 text-primary-500" data-id="element-1051" />
            Form Builder Tips
          </h3>
          <ul className="space-y-2 text-sm text-gray-600" data-id="element-1052">
            <li className="flex items-start" data-id="element-1053">
              <ArrowRightIcon className="w-3 h-3 text-primary-500 mt-1 mr-2 flex-shrink-0" data-id="element-1054" />
              Use <span className="font-medium" data-id="element-1055">eligibility rules</span> to
              automatically screen applicants based on their responses.
            </li>
            <li className="flex items-start" data-id="element-1056">
              <ArrowRightIcon className="w-3 h-3 text-primary-500 mt-1 mr-2 flex-shrink-0" data-id="element-1057" />
              Test your form in{' '}
              <span className="font-medium" data-id="element-1058">preview mode</span> to see how it
              will appear to applicants.
            </li>
            <li className="flex items-start" data-id="element-1059">
              <ArrowRightIcon className="w-3 h-3 text-primary-500 mt-1 mr-2 flex-shrink-0" data-id="element-1060" />
              Required fields are marked with an asterisk (
              <span className="text-red-500" data-id="element-1061">*</span>) for applicants.
            </li>
          </ul>
        </div>}
    </div>;
};
export default Step3ApplicationForm;